/**
 * Skeleton Loader Components
 *
 * Animated loading placeholders for better perceived performance
 * Used while data is loading from API
 */

interface SkeletonProps {
  className?: string
}

/**
 * Base Skeleton - animated gray box
 */
export function Skeleton({ className = '' }: SkeletonProps) {
  return (
    <div
      className={`animate-pulse bg-slate-200 rounded ${className}`}
      aria-label="กำลังโหลด..."
      role="status"
    />
  )
}

/**
 * Skeleton for KPI Card
 */
export function SkeletonKpiCard() {
  return (
    <div className="bg-white rounded-xl border-2 border-slate-200 shadow-sm p-6">
      {/* Icon */}
      <div className="flex items-start justify-between mb-4">
        <Skeleton className="w-12 h-12 rounded-full" />
        <Skeleton className="w-16 h-6" />
      </div>

      {/* Title */}
      <Skeleton className="h-5 w-3/4 mb-2" />

      {/* Description */}
      <Skeleton className="h-4 w-full mb-4" />
      <Skeleton className="h-4 w-2/3 mb-6" />

      {/* Value */}
      <Skeleton className="h-10 w-32 mb-4" />

      {/* Breakdown */}
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-4/6" />
      </div>
    </div>
  )
}

/**
 * Skeleton for Chart
 */
export function SkeletonChart({ height = 400 }: { height?: number }) {
  return (
    <div
      className="bg-white rounded-xl border-2 border-slate-200 shadow-sm p-6"
      style={{ height: `${height + 100}px` }}
    >
      {/* Chart Title */}
      <div className="mb-4">
        <Skeleton className="h-6 w-1/2 mb-2" />
        <Skeleton className="h-4 w-3/4" />
      </div>

      {/* Chart Area */}
      <div className="relative" style={{ height: `${height}px` }}>
        <Skeleton className="absolute inset-0" />

        {/* Simulated chart bars/lines */}
        <div className="absolute inset-0 flex items-end justify-around p-4 gap-2">
          <Skeleton className="w-full h-1/2" />
          <Skeleton className="w-full h-3/4" />
          <Skeleton className="w-full h-2/3" />
          <Skeleton className="w-full h-4/5" />
          <Skeleton className="w-full h-3/5" />
        </div>
      </div>
    </div>
  )
}

/**
 * Skeleton for Table Row
 */
export function SkeletonTableRow({ columns = 4 }: { columns?: number }) {
  return (
    <tr className="border-b border-slate-200">
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <Skeleton className="h-5 w-full" />
        </td>
      ))}
    </tr>
  )
}

/**
 * Skeleton for Table
 */
export function SkeletonTable({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Table Header */}
      <div className="bg-slate-50 border-b border-slate-200 px-4 py-3">
        <div className="flex gap-4">
          {Array.from({ length: columns }).map((_, i) => (
            <Skeleton key={i} className="h-5 flex-1" />
          ))}
        </div>
      </div>

      {/* Table Body */}
      <table className="w-full">
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <SkeletonTableRow key={i} columns={columns} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Skeleton for Dashboard Grid
 */
export function SkeletonDashboard() {
  return (
    <div className="space-y-6">
      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SkeletonKpiCard />
        <SkeletonKpiCard />
        <SkeletonKpiCard />
        <SkeletonKpiCard />
      </div>

      {/* Chart */}
      <SkeletonChart />

      {/* Table */}
      <SkeletonTable rows={8} columns={5} />
    </div>
  )
}

/**
 * Inline Loading Spinner
 */
export function LoadingSpinner({ size = 'md', className = '' }: { size?: 'sm' | 'md' | 'lg'; className?: string }) {
  const sizeClasses = {
    sm: 'w-4 h-4 border-2',
    md: 'w-8 h-8 border-3',
    lg: 'w-12 h-12 border-4',
  }

  return (
    <div
      className={`inline-block animate-spin rounded-full border-slate-300 border-t-korat-navy-700 ${sizeClasses[size]} ${className}`}
      role="status"
      aria-label="กำลังโหลด"
    >
      <span className="sr-only">กำลังโหลด...</span>
    </div>
  )
}

/**
 * Full Page Loading
 */
export function LoadingPage({ message = 'กำลังโหลดข้อมูล...' }: { message?: string }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
      <LoadingSpinner size="lg" className="mb-4" />
      <p className="text-slate-600 text-lg font-medium">{message}</p>
      <p className="text-slate-500 text-sm mt-2">กรุณารอสักครู่</p>
    </div>
  )
}
